import Element from '../components/Element';
import GeometryAlgorithm from '../geometry/GeometryAlgorithm';
import getSymbol from '../style';

/**
 * 捕捉行为类的基类，不同的捕捉行为具有不同的捕捉数据结构.
 */
export default class SnapActor extends Element {
    /**
     * 捕捉行为类初始化方法.
     * @returns {undefined}
     */
    constructor(options) {
        super({
            priority: 0, // 反馈的优先级
            tolerance: 20, // 捕捉的范围容差，单位为像素
            ...options
        });

        this.geometryAlgorithm = GeometryAlgorithm.getInstance();

        /**
         * 捕捉到的点位信息
         * @type {object<L.Point>}
         */
        this.snapPoint = null;
    }

    /**
     * 添加到地图中
     * @param {Object}  map 地图对象
     */
    addTo(map) {
        if (map && map.SnapController) {
            return super.addTo(map.SnapController);
        }
        return null;
    }

    /**
     * 添加到控制器时调用，执行初始化操作
     * @param {object} controller Controller实例
     */
    onAdd(controller) {
        super.onAdd(controller);

        this._map = controller.map;
        this._featureSelector = this._map.FeatureSelector;
        this._geojsonConverter = this._map.GeojsonConverter;
    }

    /**
     * 从控制器中删除时调用
     */
    onRemove() {
        this.clear();

        this._featureSelector = null;
        this._geojsonConverter = null;
        this._map = null;

        super.onRemove();
    }

    /**
     * 清理捕捉信息
     */
    clear() {
        this.snapPoint = null;
    }

    /**
     * 捕捉方法
     * @param {object<geojson>} point 点经纬度几何，一般是鼠标的位置
     * @returns {object} snapResult - 捕捉结果
     */
    snap(point) {
        this.clear();
    }

    /**
     * 获取捕捉时的要绘制的图标信息
     * @return {array}
     */
    getDrawItems() {
        if (this.snapPoint) {
            return [
                {
                    geometry: this.snapPoint, // geojson
                    symbol: getSymbol('snap_pt') // symbol json
                }
            ];
        }
        return [];
    }

    /**
     * 获得计算得到的最终捕捉数据结构.
     * @returns {object}.
     */
    getSnapResult() {
        if (this.snapPoint) {
            return {
                snapActor: this.constructor.name,
                point: this.snapPoint
            };
        }

        return null;
    }

    /**
     * 获取捕捉查找数据的范围.
     * @param {object<geojson>}   point 点经纬度几何对象
     * @param {number}            tol   鼠标点的捕捉范围，单位为像素
     * @returns {object<geojson>} 边长为2*tol的点的外包矩形
     */
    getBox(point, tol = this.options.tolerance) {
        const pixelPoint = this._geojsonConverter.toPoint(point);
        const left = pixelPoint.x - tol;
        const right = pixelPoint.x + tol;
        const top = pixelPoint.y - tol;
        const bottom = pixelPoint.y + tol;

        const coordinates = [];
        const leftTop = this._map.unproject([left, top]);
        const rightTop = this._map.unproject([right, top]);
        const rightBottom = this._map.unproject([right, bottom]);
        const leftBottom = this._map.unproject([left, bottom]);

        coordinates.push([leftTop.lng, leftTop.lat]);
        coordinates.push([rightTop.lng, rightTop.lat]);
        coordinates.push([rightBottom.lng, rightBottom.lat]);
        coordinates.push([leftBottom.lng, leftBottom.lat]);
        coordinates.push([leftTop.lng, leftTop.lat]);

        return {
            type: 'Polygon',
            coordinates: [coordinates]
        };
    }

    /**
     * 计算鼠标移动点和目标几何之间的距离.
     * @param {object} geometry1 - 鼠标点位几何
     * @param {object} geometry2 - 目标对象几何
     * @returns {object} - 返回计算的结果对象
     */
    distance(geometry1, geometry2) {
        return this.geometryAlgorithm.distance(geometry1, geometry2);
    }

    /**
     * 计算两个几何对象之间距离最近的两个点信息
     * @param {object} geometry1 - 鼠标点位几何
     * @param {object} geometry2 - 目标线对象几何
     * @returns {object} - 返回计算的结果对象
     */
    nearestPoints(geometry1, geometry2) {
        return this.geometryAlgorithm.nearestPoints(geometry1, geometry2);
    }

    /**
     * 计算几何的与几何对象之间距离最近的形状点信息
     * @param {object} pointGeom - 鼠标点位几何
     * @param {object} geometry  - 目标对象几何
     * @returns {object} - 返回计算的结果对象
     */
    nearestLocations(pointGeom, geometry) {
        return this.geometryAlgorithm.nearestLocations(pointGeom, geometry);
    }

    /**
     * 判断两个几何是否相交.
     * @param  {object} geometry1 - 传入的几何对象
     * @param  {object} geometry2 - 传入的几何对象
     * @return {Boolean} - 布尔值，反应两个几何是否相交
     */
    intersects(geometry1, geometry2) {
        return this.geometryAlgorithm.intersects(geometry1, geometry2);
    }
}
